// Página "Solicitudes" — lista de solicitudes del abogado al cliente
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { ClienteSolicitudCard } from '@/components/clientes/ClienteSolicitudCard';
import { InboxArrowDownIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import {
  useClienteSolicitudesPendientes,
  useClienteSolicitudesAll,
  useResponderSolicitud,
} from '@/hooks/useClienteSolicitudes';
import { clienteExpedienteService } from '@/services/clienteExpedienteService';

export default function ClientesSolicitudes() {
  const { t } = useTranslation('portal');
  const [showCompletadas, setShowCompletadas] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data: pendientes = [], isLoading: loadingPendientes } = useClienteSolicitudesPendientes();
  const { data: todas = [], isLoading: loadingTodas } = useClienteSolicitudesAll();
  const responderMutation = useResponderSolicitud();

  const handleResponder = async (expedienteId: string, solicitudId: string, texto: string, file?: File) => {
    let archivoId: string | undefined;
    if (file) {
      try {
        setUploading(true);
        const uploaded = await clienteExpedienteService.uploadDocument(expedienteId, file);
        archivoId = uploaded.id;
      } catch {
        toast.error(t('requests.uploadError'));
        return;
      } finally {
        setUploading(false);
      }
    }
    responderMutation.mutate(
      {
        expedienteId,
        solicitudId,
        data: {
          estado: 'completada',
          respuesta_texto: texto || undefined,
          respuesta_archivo_id: archivoId,
        },
      },
      {
        onSuccess: () => toast.success(t('requests.responseSent')),
        onError: () => toast.error(t('requests.responseError')),
      },
    );
  };

  const isLoading = loadingPendientes || loadingTodas;

  const completadas = todas.filter(
    (s) => s.estado === 'completada' || s.estado === 'cancelada'
  );

  if (isLoading) {
    return (
      <ClientLayout>
        <div className="space-y-4">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('requests.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('requests.subtitle')}
          </p>
        </div>

        {pendientes.length === 0 && completadas.length === 0 && (
          <div className="text-center py-16">
            <InboxArrowDownIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {t('requests.noPending')}
            </h2>
            <p className="text-gray-500 max-w-md mx-auto">
              {t('requests.noPendingDesc')}
            </p>
          </div>
        )}

        {pendientes.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              {t('requests.pendingLabel', { count: pendientes.length })}
            </h2>
            {pendientes.map((s) => (
              <ClienteSolicitudCard key={s.id} solicitud={s} onResponder={handleResponder} submitting={uploading || responderMutation.isPending} />
            ))}
          </div>
        )}

        {completadas.length > 0 && (
          <div className="space-y-3">
            <button
              onClick={() => setShowCompletadas((v) => !v)}
              className="text-sm text-primary hover:underline font-medium"
            >
              {showCompletadas ? t('requests.hideCompleted') : t('requests.showCompleted')} ({completadas.length})
            </button>
            {showCompletadas && completadas.map((s) => (
              <ClienteSolicitudCard key={s.id} solicitud={s} onResponder={handleResponder} submitting={uploading || responderMutation.isPending} />
            ))}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
